import { cn } from "@/lib/utils";
import type { ReactNode, CSSProperties } from "react";

interface BentoGridProps {
    children: ReactNode;
    className?: string;
}

interface BentoCardProps {
    children: ReactNode;
    className?: string;
    size?: "default" | "large" | "tall" | "wide";
    gradient?: string;
    style?: CSSProperties;
}

/**
 * BentoGrid - A modern asymmetric grid layout for SaaS landing pages
 * Supports different card sizes for visual hierarchy
 */
export const BentoGrid = ({ children, className }: BentoGridProps) => {
    return (
        <div
            className={cn(
                "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6",
                className
            )}
        >
            {children}
        </div>
    );
};

/**
 * BentoCard - Individual card for the bento grid
 * Supports different sizes: default (1x1), large (2x2), tall (1x2), wide (2x1)
 */
export const BentoCard = ({
    children,
    className,
    size = "default",
    gradient,
    style,
}: BentoCardProps) => {
    const sizeClasses = {
        default: "",
        large: "md:col-span-2 md:row-span-2",
        tall: "md:row-span-2",
        wide: "md:col-span-2",
    };

    return (
        <div
            className={cn(
                // Base styles
                "group relative overflow-hidden rounded-2xl md:rounded-3xl",
                // Glassmorphism
                "bg-card/40 backdrop-blur-xl",
                // Border with gradient hover
                "border border-border/30 hover:border-primary/40",
                // Shadow and hover effects
                "shadow-lg hover:shadow-2xl",
                "transition-all duration-500 ease-out",
                // Hover transform
                "hover:-translate-y-1",
                // Size
                sizeClasses[size],
                className
            )}
            style={style}
        >
            {/* Gradient overlay on hover */}
            <div
                className={cn(
                    "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500",
                    gradient || "bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"
                )}
            />

            {/* Shimmer effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />

            {/* Gradient border glow on hover */}
            <div className="absolute inset-0 rounded-2xl md:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-[-1px] rounded-2xl md:rounded-3xl bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur-sm" />
            </div>

            {/* Content */}
            <div className="relative z-10 h-full">{children}</div>
        </div>
    );
};

export default BentoGrid;
